import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { ArrowLeft, MapPin, Plus, Search, MoreHorizontal, Edit, Trash2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Mapeo = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/catastro/mapeo`, { headers });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const filteredData = data.filter(item => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      item.codigo_catastral?.toLowerCase().includes(s) ||
      item.sector?.toLowerCase().includes(s) ||
      item.manzana?.toLowerCase().includes(s) ||
      item.parcela?.toLowerCase().includes(s) ||
      item.descripcion?.toLowerCase().includes(s)
    );
  });

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/api/catastro/mapeo`, formData, { headers });
      toast.success("Registro creado exitosamente");
      setDialogOpen(false);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/api/catastro/mapeo/${editingItem.id}`, formData, { headers });
      toast.success("Registro actualizado exitosamente");
      setDialogOpen(false);
      setEditingItem(null);
      setFormData({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar registro");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/api/catastro/mapeo/${id}`, { headers });
      toast.success("Registro eliminado exitosamente");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar registro");
    }
  };

  return (
    <div className="space-y-6" data-testid="catastro-submodulo">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/catastro')} className="hover:bg-slate-100">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
          <MapPin className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Mapeo</h1>
          <p className="text-slate-500">Gestión de mapeo</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm" data-testid="registro-counter">
            <span className="font-bold text-blue-800">{filteredData.length}</span>
            <span className="text-blue-600">de</span>
            <span className="font-bold text-blue-800">{data.length}</span>
            <span className="text-blue-600">registros</span>
          </div>
          <Button onClick={() => { setEditingItem(null); setFormData({}); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-nuevo">
            <Plus className="w-4 h-4 mr-2" />Nuevo Registro
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="data-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Código Catastral</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Sector</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Manzana</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Parcela</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Descripción</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Coordenadas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Área (m²)</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">Cargando...</td></tr>
                ) : filteredData.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">No hay registros</td></tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{item.codigo_catastral}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.sector || "-"}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.manzana || "-"}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.parcela || "-"}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.descripcion || "-"}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.coordenadas || "-"}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.area_m2 || "-"}</td>
                      <td className="px-4 py-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`row-actions-${item.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setEditingItem(item); setFormData(item); setDialogOpen(true); }}><Edit className="w-4 h-4 mr-2" />Editar</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(item.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Eliminar</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Mapeo" : "Nuevo Registro - Mapeo"}</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Código Catastral *</Label>
                <Input value={formData.codigo_catastral || ""} onChange={(e) => setFormData({...formData, codigo_catastral: e.target.value})} placeholder="Ej: 01-02-03-04-05" data-testid="form-codigo-catastral-mapeo" />
              </div>
              <div className="space-y-2">
                <Label>Sector</Label>
                <Input value={formData.sector || ""} onChange={(e) => setFormData({...formData, sector: e.target.value})} placeholder="Ej: Norte, Sur, Centro" data-testid="form-sector-mapeo" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Manzana</Label>
                <Input value={formData.manzana || ""} onChange={(e) => setFormData({...formData, manzana: e.target.value})} placeholder="Ej: A, B, C o 01, 02" data-testid="form-manzana-mapeo" />
              </div>
              <div className="space-y-2">
                <Label>Parcela</Label>
                <Input value={formData.parcela || ""} onChange={(e) => setFormData({...formData, parcela: e.target.value})} placeholder="Número de parcela" data-testid="form-parcela-mapeo" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Coordenadas</Label>
                <Input value={formData.coordenadas || ""} onChange={(e) => setFormData({...formData, coordenadas: e.target.value})} placeholder="Latitud, Longitud" data-testid="form-coordenadas-mapeo" />
              </div>
              <div className="space-y-2">
                <Label>Área (m²)</Label>
                <Input type="number" value={formData.area_m2 || ""} onChange={(e) => setFormData({...formData, area_m2: e.target.value})} placeholder="Área en metros cuadrados" data-testid="form-area-mapeo" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={formData.descripcion || ""} onChange={(e) => setFormData({...formData, descripcion: e.target.value})} placeholder="Descripción del mapeo, linderos, ubicación..." rows={3} data-testid="form-descripcion-mapeo" />
            </div>
            <div className="space-y-2">
              <Label>Observaciones</Label>
              <Textarea value={formData.observaciones || ""} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} placeholder="Observaciones adicionales..." rows={2} data-testid="form-observaciones-mapeo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={editingItem ? handleUpdate : handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-guardar">{editingItem ? "Actualizar" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Mapeo;
